"use client";

import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";

interface Document {
  id: string;
  title: string;
  description: string | null;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  createdAt: string;
  category: {
    id: string;
    name: string;
    slug: string;
    parent: {
      id: string;
      name: string;
      slug: string;
    } | null;
  } | null;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  parentId: string | null;
  children?: Category[];
  _count?: {
    documents: number;
  };
}

export default function DownloadClient() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<Category[]>([]);
  const t = useTranslations("download");

  useEffect(() => {
    fetchData();
  }, []);

  // No need for separate fetchDocuments - we already have all documents loaded

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, documentsRes] = await Promise.all([
        fetch("/api/categories"),
        fetch("/api/documents?public=true"),
      ]);

      if (categoriesRes.ok) {
        const categoriesData = await categoriesRes.json();
        setCategories(categoriesData.categories || []);
      }

      if (documentsRes.ok) {
        const documentsData = await documentsRes.json();
        setDocuments(documentsData.documents || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const findCategory = (id: string, cats: Category[]): Category | null => {
    for (const cat of cats) {
      if (cat.id === id) return cat;
      if (cat.children) {
        const found = findCategory(id, cat.children);
        if (found) return found;
      }
    }
    return null;
  };

  const getBreadcrumbPath = (categoryId: string, cats: Category[]): Category[] => {
    const category = findCategory(categoryId, cats);
    if (!category) return [];

    const path: Category[] = [];
    let current: Category | null = category;

    // Build path from category to root
    while (current) {
      path.unshift(current);
      if (current.parentId) {
        current = findCategory(current.parentId, cats);
      } else {
        current = null;
      }
    }

    return path;
  };

  const handleCategoryClick = (category: Category) => {
    const path = getBreadcrumbPath(category.id, categories);
    setBreadcrumbs(path);
    setSelectedCategoryId(category.id);
    setSearchTerm(""); // Clear search when selecting category
  };

  const handleBreadcrumbClick = (category: Category) => {
    if (category.id === selectedCategoryId) return;
    
    const path = getBreadcrumbPath(category.id, categories);
    setBreadcrumbs(path);
    setSelectedCategoryId(category.id);
    setSearchTerm("");
  };

  const handleBackToRoot = () => {
    setSelectedCategoryId(null);
    setBreadcrumbs([]);
    setSearchTerm("");
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes("pdf")) {
      return (
        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }
    if (mimeType.includes("word") || mimeType.includes("document")) {
      return (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }
    if (mimeType.includes("excel") || mimeType.includes("spreadsheet")) {
      return (
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }
    return (
      <svg className="w-8 h-8 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  };

  // Get current category and its children
  const currentCategory = selectedCategoryId
    ? findCategory(selectedCategoryId, categories)
    : null;

  const currentChildren = currentCategory?.children || [];
  
  // Get direct child category IDs (for filtering documents that are only in subcategories, not current)
  const childCategoryIds = currentChildren.map((c) => c.id);
  
  const currentDocuments = documents.filter((doc) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        doc.title.toLowerCase().includes(term) ||
        doc.description?.toLowerCase().includes(term) ||
        doc.fileName.toLowerCase().includes(term) ||
        doc.category?.name.toLowerCase().includes(term)
      );
    }
    
    if (!selectedCategoryId) return false;
    
    // Show documents that are directly in this category (not in subcategories)
    // This means the document's category ID matches selectedCategoryId exactly
    return doc.category?.id === selectedCategoryId;
  });

  // Filter root categories (no parent)
  const rootCategories = selectedCategoryId === null
    ? categories.filter((cat) => !cat.parentId)
    : [];

  // Get all category IDs including children (for counting documents)
  const getAllCategoryIds = (category: Category): string[] => {
    const ids = [category.id];
    if (category.children) {
      category.children.forEach((child) => {
        ids.push(...getAllCategoryIds(child));
      });
    }
    return ids;
  };

  // Count documents in category and all its subcategories
  const getTotalSubcategoryDocuments = (category: Category): number => {
    const allIds = getAllCategoryIds(category);
    return documents.filter((doc) => allIds.includes(doc.category?.id || "")).length;
  };

  // Count direct documents in category (not in subcategories)
  const getDirectCategoryDocuments = (categoryId: string): number => {
    return documents.filter((doc) => {
      if (!doc.category) return false;
      // Direct match
      if (doc.category.id === categoryId) return true;
      return false;
    }).length;
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold mb-2">{t("title")}</h1>
          <p className="text-primary-100">{t("subtitle")}</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumbs */}
        {(breadcrumbs.length > 0 || selectedCategoryId) && (
          <nav className="mb-6 flex items-center gap-2 text-sm">
            <button
              onClick={handleBackToRoot}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              {t("allCategories")}
            </button>
            {breadcrumbs.map((cat, index) => (
              <span key={cat.id} className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                {index === breadcrumbs.length - 1 ? (
                  <span className="text-secondary-900 font-semibold">{cat.name}</span>
                ) : (
                  <button
                    onClick={() => handleBreadcrumbClick(cat)}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    {cat.name}
                  </button>
                )}
              </span>
            ))}
          </nav>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("searchPlaceholder")}
              className="w-full px-4 py-3 pl-12 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <svg
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : searchTerm ? (
          /* Search Results */
          <div className="space-y-4">
            {currentDocuments.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-secondary-200">
                <p className="text-secondary-600">{t("noSearchResults")}</p>
              </div>
            ) : (
              currentDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="bg-white rounded-xl p-6 shadow-sm border border-secondary-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">{getFileIcon(doc.mimeType)}</div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-secondary-900 mb-1">
                        {doc.title}
                      </h3>
                      {doc.description && (
                        <p className="text-secondary-600 mb-2">{doc.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-secondary-500">
                        <span>{doc.fileName}</span>
                        <span>•</span>
                        <span>{formatFileSize(doc.fileSize)}</span>
                        <span>•</span>
                        <span>{new Date(doc.createdAt).toLocaleDateString("sr-RS")}</span>
                        {doc.category && (
                          <>
                            <span>•</span>
                            <span className="text-primary-600">{doc.category.name}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <a
                        href={doc.filePath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        {t("open")}
                      </a>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : selectedCategoryId === null ? (
          /* Root Categories View */
          <div>
            {rootCategories.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-secondary-200">
                <h3 className="text-xl font-semibold text-secondary-900 mb-2">{t("noCategories")}</h3>
                <p className="text-secondary-600">{t("noCategoriesDescription")}</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rootCategories.map((category) => {
                  const docCount = getTotalSubcategoryDocuments(category);
                  return (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryClick(category)}
                      className="bg-white rounded-xl p-6 shadow-sm border border-secondary-200 hover:shadow-md hover:border-primary-300 transition-all text-left"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-semibold text-secondary-900">
                          {category.name}
                        </h3>
                        <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      {category.description && (
                        <p className="text-sm text-secondary-600 mb-3">{category.description}</p>
                      )}
                      <div className="flex items-center gap-2 text-sm text-secondary-500">
                        <span>{docCount} {t("documents")}</span>
                        {category.children && category.children.length > 0 && (
                          <>
                            <span>•</span>
                            <span>{category.children.length} {t("subcategories")}</span>
                          </>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* Category View - Shows subcategories and documents */
          <div className="space-y-6">
            {/* Subcategories */}
            {currentChildren.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-secondary-900 mb-4">{t("subcategories")}</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {currentChildren.map((category) => {
                    const docCount = getTotalSubcategoryDocuments(category);
                    return (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryClick(category)}
                        className="bg-white rounded-xl p-4 shadow-sm border border-secondary-200 hover:shadow-md hover:border-primary-300 transition-all text-left"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-secondary-900">{category.name}</h3>
                          <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                        {category.description && (
                          <p className="text-xs text-secondary-600 mb-2">{category.description}</p>
                        )}
                        <div className="text-xs text-secondary-500">
                          {docCount} {t("documents")}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Documents in current category */}
            {currentDocuments.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-secondary-900 mb-4">
                  {currentChildren.length > 0 ? t("documents") : t("allDocuments")}
                </h2>
                <div className="space-y-4">
                  {currentDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      className="bg-white rounded-xl p-6 shadow-sm border border-secondary-200 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">{getFileIcon(doc.mimeType)}</div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-secondary-900 mb-1">
                            {doc.title}
                          </h3>
                          {doc.description && (
                            <p className="text-secondary-600 mb-2">{doc.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-secondary-500">
                            <span>{doc.fileName}</span>
                            <span>•</span>
                            <span>{formatFileSize(doc.fileSize)}</span>
                            <span>•</span>
                            <span>{new Date(doc.createdAt).toLocaleDateString("sr-RS")}</span>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <a
                            href={doc.filePath}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            {t("open")}
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {currentChildren.length === 0 && currentDocuments.length === 0 && (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-secondary-200">
                <p className="text-secondary-600">{t("noDocumentsInCategory")}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
